import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../stores/appStore';
import { useAuthStore } from '../../stores/authStore';
import { useEmailStore } from '../../stores/email-store';
import { useTodoStore } from '../../stores/todo-store';
import { memoryApi, type MemoryContext } from '../../lib/memory-api';
import { EmailFocus } from './EmailFocus';
import { TodoFocus } from './TodoFocus';
import { CatchupPane } from './CatchupPane';
import { FocusHeader } from './FocusHeader';

/**
 * Focus pane: slides in from the right when an email or todo is active.
 * Two-column internal layout: entity content left, catch-up memory right.
 * Replaces the old narrow CatchupPanel overlay.
 */

const PANE_WIDTH_SUBTRACT = 0; // Sidebar width is already outside the main area.
const PANE_MAX_WIDTH = 1180;

export function FocusPane() {
  const activeEmailId = useAppStore((s) => s.activeEmailId);
  const activeTodoId = useAppStore((s) => s.activeTodoId);
  const activeNoteId = useAppStore((s) => s.activeNoteId);
  const setActiveEmailId = useAppStore((s) => s.setActiveEmailId);
  const setActiveTodoId = useAppStore((s) => s.setActiveTodoId);
  const setActiveNoteId = useAppStore((s) => s.setActiveNoteId);

  const emails = useEmailStore((s) => s.emails);
  const currentEmail = useEmailStore((s) => s.currentEmail);
  const loadEmail = useEmailStore((s) => s.loadEmail);
  const todos = useTodoStore((s) => s.todos);
  const token = useAuthStore((s) => s.token);

  const [context, setContext] = useState<MemoryContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDemo = !token || token === 'demo-token';

  // Resolve the focused entity. Priority: email > todo > note
  const focused = useMemo((): FocusedEntity | null => {
    if (activeEmailId) {
      const e = emails.find((x) => x.id === activeEmailId);
      if (e) return { kind: 'email', email: e };
      return { kind: 'email-missing', id: activeEmailId };
    }
    if (activeTodoId) {
      const t = todos.find((x) => x.id === activeTodoId);
      if (t) return { kind: 'todo', todo: t };
      return { kind: 'todo-missing', id: activeTodoId };
    }
    if (activeNoteId) {
      return { kind: 'note', id: activeNoteId };
    }
    return null;
  }, [activeEmailId, activeTodoId, activeNoteId, emails, todos]);

  const focusKey = focusedKey(focused);

  // Fetch memory context whenever the focused entity changes
  useEffect(() => {
    if (!focused) {
      setContext(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isDemo) {
          await new Promise((r) => setTimeout(r, 200));
          if (!cancelled) setContext(demoContextFor(focused));
          return;
        }
        const params = paramsFor(focused);
        if (!params) {
          if (!cancelled) setContext(null);
          return;
        }
        const ctx = await memoryApi.getContext(params);
        if (!cancelled) setContext(ctx);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load context');
          setContext(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [focusKey, isDemo]);

  const close = useCallback(() => {
    setActiveEmailId(null);
    setActiveTodoId(null);
    setActiveNoteId(null);
  }, [setActiveEmailId, setActiveTodoId, setActiveNoteId]);

  // Fetch full email body when an email is focused
  useEffect(() => {
    if (activeEmailId) {
      loadEmail(activeEmailId).catch(() => {});
    }
  }, [activeEmailId, loadEmail]);

  // Escape to close
  useEffect(() => {
    if (!focused) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focused, close]);

  if (!focused) return null;

  return (
    <>
      {/* Scrim */}
      <div
        onClick={close}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(2px)',
          zIndex: 20,
          animation: 'signa-fade-in 0.22s ease-out',
        }}
      />
      {/* Pane */}
      <aside
        role="dialog"
        aria-label="Focus pane"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: `calc(100% - ${PANE_WIDTH_SUBTRACT}px)`,
          maxWidth: PANE_MAX_WIDTH,
          background: 'var(--paper)',
          borderLeft: '1px solid var(--rule)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 21,
          display: 'flex',
          animation: 'signa-slide-in 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)',
          overflow: 'hidden',
        }}
      >
        {focused.kind === 'email' && (
          <EmailFocus
            email={focused.email}
            fullEmail={currentEmail?.id === focused.email.id ? currentEmail : null}
            context={context}
            loading={loading}
            error={error}
            onClose={close}
          />
        )}
        {focused.kind === 'todo' && (
          <TodoFocus
            todo={focused.todo}
            context={context}
            loading={loading}
            error={error}
            onClose={close}
          />
        )}
        {(focused.kind === 'email-missing' ||
          focused.kind === 'todo-missing' ||
          focused.kind === 'note') && (
          <MissingEntity kind={focused.kind} id={focused.id} onClose={close} />
        )}
      </aside>
    </>
  );
}

// Missing entity placeholder

function MissingEntity({
  kind,
  id,
  onClose,
}: {
  kind: 'email-missing' | 'todo-missing' | 'note';
  id: string;
  onClose: () => void;
}) {
  const label =
    kind === 'email-missing' ? 'Email' : kind === 'todo-missing' ? 'Todo' : 'Note';
  return (
    <>
      <div
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}
      >
        <FocusHeader onClose={onClose} />
        <div
          style={{
            padding: '40px 44px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            color: 'var(--ink-soft)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
              color: 'var(--ink-faint)',
            }}
          >
            {label}
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 26,
              letterSpacing: '-0.4px',
              color: 'var(--ink)',
              fontWeight: 500,
              margin: '0 0 8px 0',
            }}
          >
            Not in this cache
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 520, margin: 0 }}>
            The referenced {label.toLowerCase()} (<code>{id}</code>) is not in
            the current store. In production this would hydrate from the
            provider or memory backend; in demo mode it simply means the
            finding pointed to an entity we do not have sample data for.
          </p>
        </div>
      </div>
      <CatchupPane kind="note" context={null} />
    </>
  );
}

// Types and helpers

type FocusedEntity =
  | { kind: 'email'; email: ReturnType<typeof useEmailStore.getState>['emails'][number] }
  | { kind: 'email-missing'; id: string }
  | { kind: 'todo'; todo: ReturnType<typeof useTodoStore.getState>['todos'][number] }
  | { kind: 'todo-missing'; id: string }
  | { kind: 'note'; id: string };

function focusedKey(f: FocusedEntity | null): string | null {
  if (!f) return null;
  if (f.kind === 'email') return `email:${f.email.id}`;
  if (f.kind === 'email-missing') return `email-missing:${f.id}`;
  if (f.kind === 'todo') return `todo:${f.todo.id}`;
  if (f.kind === 'todo-missing') return `todo-missing:${f.id}`;
  return `note:${f.id}`;
}

function paramsFor(
  f: FocusedEntity
): { sender?: string; subject?: string; taskId?: string } | null {
  if (f.kind === 'email') {
    return { sender: f.email.fromEmail, subject: f.email.subject };
  }
  if (f.kind === 'todo') {
    return { taskId: f.todo.id };
  }
  return null;
}

// Demo content

function demoContextFor(f: FocusedEntity): MemoryContext {
  const now = new Date();
  const daysAgo = (d: number) => new Date(now.getTime() - d * 864e5).toISOString();

  if (f.kind === 'email') {
    const who = f.email.fromName || f.email.fromEmail;
    return {
      person: [
        {
          collection: 'people',
          content: `${who} last replied 21 days ago. Tone read: warm, getting anxious as things pile up.`,
          metadata: { type: 'context', createdAt: daysAgo(21) },
        },
        {
          collection: 'people',
          content: 'Prefers Loom over meetings. Focus hours: 9-12 daily.',
          metadata: { type: 'context', createdAt: daysAgo(60) },
        },
      ],
      commitments: [
        {
          collection: 'capture',
          content: 'You promised "end of week" feedback on the Q2 draft (Mar 20).',
          metadata: { type: 'commitment', createdAt: daysAgo(28) },
        },
      ],
      meetings: [
        {
          collection: 'capture',
          content: 'Jan planning session scoped Q2 deliverables with her team.',
          metadata: { type: 'meeting_note', createdAt: daysAgo(90) },
        },
      ],
      insights: [
        {
          collection: 'capture',
          content: 'Your note from Mar 19: "push back on the budget line; too loose."',
          metadata: { type: 'insight', createdAt: daysAgo(29) },
        },
      ],
      related: [
        {
          collection: 'email',
          content: 'Prior thread: "v1 draft feedback" with 4 messages, Feb 18-22.',
          metadata: { type: 'context', createdAt: daysAgo(55) },
        },
      ],
    };
  }

  if (f.kind === 'todo') {
    return {
      person: [],
      commitments: [
        {
          collection: 'capture',
          content: 'You committed to ship this before Thursday\'s standup.',
          metadata: { type: 'commitment', createdAt: daysAgo(2) },
        },
      ],
      meetings: [
        {
          collection: 'capture',
          content: 'Sprint retro flagged this task high-priority with 2 reviewers needed.',
          metadata: { type: 'meeting_note', createdAt: daysAgo(5) },
        },
      ],
      insights: [],
      related: [
        {
          collection: 'email',
          content: 'Email thread with Priya references the same deliverable.',
          metadata: { type: 'context', createdAt: daysAgo(7) },
        },
      ],
    };
  }

  return {
    person: [],
    commitments: [],
    meetings: [],
    insights: [],
    related: [],
  };
}
